import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Building2, CheckCircle2, Clock, MapPin, DollarSign, User, Phone, Mail, Calendar, Loader2, AlertCircle, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminZellePayments() {
  const [pendingPayments, setPendingPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [verifying, setVerifying] = useState(null);

  useEffect(() => {
    fetchPendingPayments();
  }, []);

  const fetchPendingPayments = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/admin/zelle-pending`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setPendingPayments(data);
      } else {
        toast.error('Failed to fetch pending payments');
      }
    } catch (err) {
      console.error('Error fetching pending payments:', err);
      toast.error('Failed to load pending payments');
    } finally {
      setLoading(false);
    }
  };

  const verifyPayment = async (rideId) => {
    setVerifying(rideId);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/admin/verify-zelle/${rideId}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        toast.success('Zelle payment verified successfully!');
        // Remove from list
        setPendingPayments(prev => prev.filter(p => p.id !== rideId));
      } else {
        const data = await response.json();
        toast.error(data.detail || 'Failed to verify payment');
      }
    } catch (err) {
      console.error('Error verifying payment:', err);
      toast.error('Failed to verify payment');
    } finally {
      setVerifying(null);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric', 
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount || 0);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Building2 className="h-6 w-6 text-purple-600" />
            Pending Zelle Payments
          </h1>
          <p className="text-gray-500 mt-1">
            Review and verify Zelle payments from customers
          </p>
        </div>
        <Button 
          variant="outline" 
          onClick={fetchPendingPayments}
          disabled={loading}
        >
          <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-purple-600" />
          <span className="ml-2 text-gray-500">Loading pending payments...</span>
        </div>
      ) : pendingPayments.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <CheckCircle2 className="h-12 w-12 text-green-500 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900">All caught up!</h3>
            <p className="text-gray-500 mt-1">No pending Zelle payments to verify</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          <div className="bg-gold-50 border border-gold-200 rounded-lg p-4 flex items-start gap-3">
            <AlertCircle className="h-5 w-5 text-gold-600 mt-0.5" />
            <div>
              <p className="font-medium text-gold-800">
                {pendingPayments.length} payment{pendingPayments.length > 1 ? 's' : ''} pending verification
              </p>
              <p className="text-sm text-gold-700 mt-1">
                Check your Zelle account to confirm these payments were received before verifying.
              </p>
            </div>
          </div>

          {pendingPayments.map((payment) => (
            <Card key={payment.id} className="border-l-4 border-l-purple-500">
              <CardHeader className="pb-2">
                <div className="flex items-start justify-between">
                  <div>
                    <CardTitle className="text-lg flex items-center gap-2">
                      <User className="h-4 w-4" />
                      {payment.client_name}
                    </CardTitle>
                    <CardDescription className="flex items-center gap-4 mt-1">
                      <span className="flex items-center gap-1">
                        <Mail className="h-3 w-3" />
                        {payment.client_email}
                      </span>
                      {payment.client_phone && (
                        <span className="flex items-center gap-1">
                          <Phone className="h-3 w-3" />
                          {payment.client_phone}
                        </span>
                      )}
                    </CardDescription>
                  </div>
                  <div className="text-right">
                    <div className="text-2xl font-bold text-purple-600">
                      {formatCurrency(payment.total_amount)}
                    </div>
                    {payment.tips > 0 && (
                      <div className="text-sm text-gray-500">
                        (includes ${payment.tips.toFixed(2)} tip)
                      </div>
                    )}
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Ride Details */}
                <div className="bg-gray-50 rounded-lg p-4 space-y-2">
                  <div className="flex items-start gap-2">
                    <MapPin className="h-4 w-4 text-green-600 mt-0.5" />
                    <div>
                      <p className="text-xs text-gray-500">Pickup</p>
                      <p className="text-sm">{payment.pickup_location}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-2">
                    <MapPin className="h-4 w-4 text-red-600 mt-0.5" />
                    <div>
                      <p className="text-xs text-gray-500">Dropoff</p>
                      <p className="text-sm">{payment.dropoff_location}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Calendar className="h-4 w-4 text-blue-600" />
                    <div>
                      <p className="text-xs text-gray-500">Scheduled</p>
                      <p className="text-sm">{formatDate(payment.date)}</p>
                    </div>
                  </div>
                </div>

                {/* Zelle Info */}
                <div className="bg-purple-50 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Clock className="h-4 w-4 text-purple-600" />
                    <span className="text-sm font-medium text-purple-800">Zelle Payment Submitted</span>
                  </div>
                  <p className="text-sm text-purple-700">
                    Submitted on: {formatDate(payment.zelle_pending_at)}
                  </p>
                  <p className="text-sm text-purple-600 mt-1">
                    Reference: <strong>{payment.id.slice(0, 8).toUpperCase()}</strong> - {payment.client_name}
                  </p>
                </div>

                {/* Actions */}
                <div className="flex gap-3 pt-2">
                  <Button
                    onClick={() => verifyPayment(payment.id)}
                    disabled={verifying === payment.id}
                    className="flex-1 bg-green-600 hover:bg-green-700"
                  >
                    {verifying === payment.id ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Verifying...
                      </>
                    ) : (
                      <>
                        <CheckCircle2 className="h-4 w-4 mr-2" />
                        Verify Payment Received
                      </>
                    )}
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
